import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { MessageCircle, X, Send, Sparkles } from "lucide-react";
import { sendChat } from "@/lib/api";

const GREETING = {
  role: "assistant",
  content:
    "Bonjour 👋 Je suis l'assistant MerchStudio. Dites-moi votre projet (produit, quantité, marquage) et je vous conseille + estime le tarif. Comment puis-je aider ?",
};

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([GREETING]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [session, setSession] = useState(() => localStorage.getItem("ms_chat_session") || null);
  const endRef = useRef(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading, open]);

  const send = async () => {
    const text = input.trim();
    if (!text || loading) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }]);
    setLoading(true);
    try {
      const res = await sendChat(session, text);
      if (res.session_id && res.session_id !== session) {
        setSession(res.session_id);
        localStorage.setItem("ms_chat_session", res.session_id);
      }
      setMessages((m) => [...m, { role: "assistant", content: res.reply }]);
    } catch {
      setMessages((m) => [...m, { role: "assistant", content: "Oups, je suis momentanément indisponible. Réessayez dans un instant." }]);
    } finally {
      setLoading(false);
    }
  };

  const suggestions = ["Prix pour 50 t-shirts brodés ?", "Quoi offrir pour un événement ?", "Différence broderie / sérigraphie ?"];

  return (
    <>
      <button
        data-testid="chat-toggle"
        onClick={() => setOpen((o) => !o)}
        className="fixed bottom-6 right-6 z-[60] w-14 h-14 flex items-center justify-center bg-[#0044ff] text-white shadow-2xl hover:scale-105 transition-transform"
        aria-label="Assistant"
      >
        {open ? <X className="w-6 h-6" /> : <MessageCircle className="w-6 h-6" />}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.98 }}
            transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
            className="fixed bottom-24 right-6 z-[60] w-[calc(100vw-3rem)] sm:w-[380px] h-[520px] max-h-[70vh] bg-[#0a0a0a] text-white flex flex-col shadow-2xl border border-white/10"
            data-testid="chat-panel"
          >
            <div className="px-5 py-4 border-b border-white/10 flex items-center gap-3">
              <div className="w-9 h-9 bg-[#0044ff] flex items-center justify-center"><Sparkles className="w-4 h-4" /></div>
              <div>
                <p className="font-display font-bold uppercase tracking-tight leading-none">Assistant MerchStudio</p>
                <p className="font-mono text-[10px] uppercase tracking-widest text-white/40 mt-1">Conseils · tarifs · devis</p>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3" data-testid="chat-messages">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[85%] px-3.5 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${m.role === "user" ? "bg-[#0044ff] text-white" : "bg-white/10 text-white/90"}`}>
                    {m.content}
                  </div>
                </div>
              ))}
              {loading && (
                <div className="flex justify-start">
                  <div className="bg-white/10 px-3.5 py-3 flex gap-1">
                    {[0, 1, 2].map((d) => (
                      <span key={d} className="w-1.5 h-1.5 bg-white/60 rounded-full animate-bounce" style={{ animationDelay: `${d * 0.15}s` }} />
                    ))}
                  </div>
                </div>
              )}
              {messages.length === 1 && (
                <div className="pt-2 space-y-2">
                  {suggestions.map((s) => (
                    <button key={s} onClick={() => setInput(s)} className="block w-full text-left text-xs font-mono uppercase tracking-wide text-white/60 border border-white/15 px-3 py-2 hover:bg-white/5 transition-colors">
                      {s}
                    </button>
                  ))}
                </div>
              )}
              <div ref={endRef} />
            </div>

            <div className="p-3 border-t border-white/10 flex items-center gap-2">
              <input
                data-testid="chat-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && send()}
                placeholder="Votre question…"
                className="flex-1 bg-white/5 border border-white/10 px-3 py-2.5 text-sm outline-none focus:border-[#0044ff] transition-colors placeholder:text-white/30"
              />
              <button data-testid="chat-send" onClick={send} disabled={loading} className="w-10 h-10 flex items-center justify-center bg-[#0044ff] hover:bg-[#0033cc] transition-colors disabled:opacity-50">
                <Send className="w-4 h-4" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
